"use client";

import React from "react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Info, Maximize, Layers, Box, Cpu } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const galleryItems = [{
  id: 1,
  title: "G657A2 Optical Fiber",
  category: "Connectivity",
  description: "Ultra-low attenuation fiber optimized for high-density AI data center interconnects.",
  polycount: "0.18 dB/km",
  islands: "125μm Clad",
  efficiency: "99.9% Purity",
  icon: Cpu,
  imageTextured: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604101/img/engine_textured_render.png",
  imageUv: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604101/img/engine_uv_layout.png"
}, {
  id: 2,
  title: "NX-Frame Chassis",
  category: "FPV Systems",
  description: "High-modulus carbon fiber frame engineered for extreme durability and aerodynamic agility.",
  polycount: "3K Carbon",
  islands: "220mm Base",
  efficiency: "Ultra-Light",
  icon: Layers,
  imageTextured: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604101/img/fpv_drone_frame_exterior.png",
  imageUv: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604101/img/fpv_drone_frame_internal.png"
}, {
  id: 3,
  title: "AI Data Interconnect",
  category: "Infrastructure",
  description: "Next-generation signal transmission assembly for high-bandwidth computing clusters.",
  polycount: "400G Ready",
  islands: "<1ms Latency",
  efficiency: "100% Reliable",
  icon: Box,
  imageTextured: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604101/img/scifi_crate_textured_render.png",
  imageUv: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604101/img/scifi_crate_uv_layout.png"
}];
export default function VisualGallery() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="141208" id="component-gallery" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4 uppercase">
            Precision <span className="text-primary">Engineering</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            Explore the internal architecture of our high-performance components. From optical core purity to drone structural integrity, Nexel defines the future of hardware.
          </p>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 transition-all duration-1000 delay-200 ease-out ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {galleryItems.map((item, i) => <Card key={item.id} data-index={i} className="group overflow-hidden border-border/20 bg-card/30 backdrop-blur-xl hover:border-primary/60 transition-all duration-500 shadow-lg">
              <div className="relative overflow-hidden cursor-crosshair">
                <AspectRatio ratio={4 / 3} className="bg-muted/5">
                  
                  {}
                  <img src={i === 0 ? "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604101/img/g657a2_diagram.png" : item.imageTextured} alt={`${item.title} Exterior`} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ease-in-out group-hover:opacity-0 z-10" />
                  
                  {}
                  <img src={i === 0 ? "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604101/img/g657a1_diagram.png" : item.imageUv} alt={`${item.title} Internal Schematic`} className="absolute inset-0 w-full h-full object-cover opacity-0 scale-100 transition-all duration-700 ease-out group-hover:opacity-100 group-hover:scale-110 z-20" />

                  {}
                  <div className="absolute top-4 left-4 z-30 flex flex-col gap-2">
                    <div className="bg-background/90 backdrop-blur-md border border-border/30 text-[10px] font-mono px-3 py-1 rounded-sm text-foreground transition-opacity duration-300 group-hover:opacity-0 uppercase tracking-widest">
                      Exterior View
                    </div>
                    <div className="bg-primary/30 backdrop-blur-md border border-primary/50 text-[10px] font-mono px-3 py-1 rounded-sm text-primary-foreground absolute top-0 left-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100 flex items-center gap-1.5 uppercase tracking-widest">
                      <Maximize className="w-3 h-3" />
                      Internal Analysis
                    </div>
                  </div>

                </AspectRatio>
              </div>

              <CardHeader className="pb-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <item.icon className="w-4 h-4 text-primary" />
                      <span className="text-[10px] font-bold text-primary uppercase tracking-widest">
                        {item.category}
                      </span>
                    </div>
                    <CardTitle className="text-xl font-bold tracking-tight">{item.title}</CardTitle>
                  </div>
                  
                  <HoverCard>
                    <HoverCardTrigger asChild>
                      <button className="p-2 rounded-md hover:bg-secondary/50 transition-colors text-muted-foreground hover:text-primary">
                        <Info className="w-5 h-5" />
                        <span className="sr-only">Technical Specifications</span>
                      </button>
                    </HoverCardTrigger>
                    <HoverCardContent className="w-64 bg-popover/95 backdrop-blur-2xl border-border/30 shadow-2xl">
                      <div className="space-y-3">
                        <h4 className="text-xs font-bold uppercase tracking-widest text-primary">Technical Specs</h4>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="text-muted-foreground font-sans">Performance:</div>
                          <div className="font-mono text-right text-foreground">{item.polycount}</div>
                          <div className="text-muted-foreground font-sans">Form Factor:</div>
                          <div className="font-mono text-right text-foreground">{item.islands}</div>
                          <div className="text-muted-foreground font-sans">Efficiency:</div>
                          <div className="font-mono text-right text-primary font-bold">{item.efficiency}</div>
                        </div>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                  {item.description}
                </CardDescription>
              </CardContent>
            </Card>)}
        </div>
      </div>
    </section>;
}